// Phase JA.2 — useDailyAccaSignals hook.
//
// Anon-readable Supabase query against `public.daily_acca_signals`
// returning rows where `suppression_state = 'published'` AND
// `kickoff_at >= now()`. RLS already enforces the published-only
// rule for anon/authenticated; we re-apply the kickoff predicate
// client-side for clarity (and to skip server-cached published rows
// that have already kicked off).
//
// Doctrine carry-forward:
//   - No service-role; anon JWT only.
//   - `reasoning_text` is server-side guarded (Phase B forbidden-vocab
//     pipeline). Do NOT re-process client-side; surface verbatim.
//   - Never fabricate. Empty list when no published rows.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export type SignalTier = "tier_a" | "tier_b" | "tier_c";
export type SignalConfidenceTier = "HIGH" | "SOLID" | "VALUE" | "STATISTICAL";
export type SignalOutcome = "pending" | "won" | "lost" | "void";

export interface DailyAccaSignal {
  signal_id: string;
  signal_date: string;
  tier: SignalTier;
  fixture_id: string | null;
  fixture_name: string;
  league: string | null;
  continent: string | null;
  kickoff_at: string;
  market: string;
  selection: string;
  odds: number;
  reasoning_text: string;
  conviction_score: number | null;
  confidence_tier: SignalConfidenceTier | null;
  agent_consensus: unknown;
  clv_edge_detected: boolean;
  clv_edge_pct: number | null;
  is_founders_pick: boolean;
  outcome: SignalOutcome;
  published_at: string | null;
}

export interface UseDailyAccaSignalsState {
  signals: DailyAccaSignal[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function asNum(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function asNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function asTier(v: unknown): SignalTier | null {
  return v === "tier_a" || v === "tier_b" || v === "tier_c" ? v : null;
}

function asConfidenceTier(v: unknown): SignalConfidenceTier | null {
  return v === "HIGH" || v === "SOLID" || v === "VALUE" || v === "STATISTICAL" ? v : null;
}

function asOutcome(v: unknown): SignalOutcome {
  return v === "won" || v === "lost" || v === "void" ? v : "pending";
}

/** Pure normalizer — exported for testing. */
export function normaliseSignal(row: Record<string, unknown>): DailyAccaSignal | null {
  const signal_id = typeof row["signal_id"] === "string" ? row["signal_id"] : null;
  const tier = asTier(row["tier"]);
  const kickoff_at = typeof row["kickoff_at"] === "string" ? row["kickoff_at"] : null;
  const fixture_name = typeof row["fixture_name"] === "string" ? row["fixture_name"] : null;
  const market = typeof row["market"] === "string" ? row["market"] : null;
  const selection = typeof row["selection"] === "string" ? row["selection"] : null;
  const reasoning_text = typeof row["reasoning_text"] === "string" ? row["reasoning_text"] : null;
  if (!signal_id || !tier || !kickoff_at || !fixture_name || !market || !selection || !reasoning_text) {
    return null;
  }
  return {
    signal_id,
    signal_date: typeof row["signal_date"] === "string" ? row["signal_date"] : "",
    tier,
    fixture_id: typeof row["fixture_id"] === "string" ? row["fixture_id"] : null,
    fixture_name,
    league: typeof row["league"] === "string" ? row["league"] : null,
    continent: typeof row["continent"] === "string" ? row["continent"] : null,
    kickoff_at,
    market,
    selection,
    odds: asNum(row["odds"]),
    reasoning_text,
    conviction_score: asNumOrNull(row["conviction_score"]),
    confidence_tier: asConfidenceTier(row["confidence_tier"]),
    agent_consensus: row["agent_consensus"] ?? null,
    clv_edge_detected: row["clv_edge_detected"] === true,
    clv_edge_pct: asNumOrNull(row["clv_edge_pct"]),
    is_founders_pick: row["is_founders_pick"] === true,
    outcome: asOutcome(row["outcome"]),
    published_at: typeof row["published_at"] === "string" ? row["published_at"] : null,
  };
}

const TIER_ORDER: Record<SignalTier, number> = { tier_a: 0, tier_b: 1, tier_c: 2 };

/** Sort: tier A first, then B, then C; within tier, by kickoff ascending. */
export function sortSignals(rows: DailyAccaSignal[]): DailyAccaSignal[] {
  return [...rows].sort((a, b) => {
    const t = TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
    if (t !== 0) return t;
    return new Date(a.kickoff_at).getTime() - new Date(b.kickoff_at).getTime();
  });
}

export function useDailyAccaSignals(): UseDailyAccaSignalsState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const instanceId = useId();
  const [signals, setSignals] = useState<DailyAccaSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    const nowISO = new Date().toISOString();

    // db/types.ts may not include daily_acca_signals yet; cast.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const res = await sb
      .from("daily_acca_signals")
      .select(
        "signal_id, signal_date, published_at, tier, fixture_id, fixture_name, league, continent, kickoff_at, market, selection, odds, reasoning_text, conviction_score, confidence_tier, agent_consensus, clv_edge_detected, clv_edge_pct, is_founders_pick, outcome",
      )
      .eq("suppression_state", "published")
      .gte("kickoff_at", nowISO)
      .order("kickoff_at", { ascending: true });

    if (res.error) {
      setError(res.error.message ?? "Failed to load signals");
      setSignals([]);
      setLoading(false);
      return;
    }
    const rows = Array.isArray(res.data) ? (res.data as Array<Record<string, unknown>>) : [];
    const normalised = rows
      .map(normaliseSignal)
      .filter((r): r is DailyAccaSignal => r !== null);
    setSignals(sortSignals(normalised));
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // JA.4 — realtime: when a published signal's outcome flips
  // pending → won/lost/void, refetch so the UI shows settlement live.
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    // Per-mount unique channel name. Supabase's channel registry
    // returns the SAME object for repeated calls with the same name,
    // so a fixed name throws on StrictMode double-mount when `.on()`
    // is called again after `.subscribe()`.
    const channelName = `daily_acca_signals:public:${instanceId}`;
    const channel = sb
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "daily_acca_signals",
        },
        () => {
          void refetch();
        },
      )
      .subscribe();
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).removeChannel(channel);
    };
  }, [supabase, refetch, instanceId]);

  return useMemo(
    () => ({ signals, loading, error, refetch }),
    [signals, loading, error, refetch],
  );
}
